import type { VisualEffect } from '@/types/effects';
import { VideoTemplate } from "@/types/template";
import { generateAllTemplates } from './template-generator';
import { TEMPLATE_LAYOUTS } from './layouts';
import { TEMPLATE_THEMES } from './themes';
import type { TemplateOptions } from '../types';

const AUTO_GENERATED_TEMPLATES = generateAllTemplates();

const LEGACY_TEMPLATES: VideoTemplate[] = [
    {
        id: 'basic',
        name: 'Clean White Bottom',
        description: '흰 글자 + 검정 외곽선, 하단형',
        layoutId: 'basic',
        themeId: 'modern_blue',
        version: '1.0.0',
        layout: {
            x: 0,
            y: 0,
            width: 1080,
            height: 1920,
            scale: 1,
            objectFit: 'contain',
        },
        textStyle: {
            fontFamily: 'Jua',
            fontSize: 68,
            color: '#ffffff',
            width: 1080,
            backgroundColor: 'rgba(0, 0, 0, 0)',
            captionY: 0.82,
            stroke: {
                width: 6,
                color: '#000000',
            },
        }
    },
    {
        id: 'basic-clean-top',
        name: 'Clean White Top',
        description: '흰 글자 + 검정 외곽선, 상단형',
        layoutId: 'basic',
        themeId: 'modern_blue',
        version: '1.0.0',
        layout: {
            x: 0,
            y: 0,
            width: 1080,
            height: 1920,
            scale: 1,
            objectFit: 'contain',
        },
        textStyle: {
            fontFamily: 'Nanum Pen Script',
            fontSize: 78,
            width: 1080,
            color: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0)',
            captionY: 0.18,
            stroke: {
                width: 6,
                color: '#000000',
            },
        }
    },
    {
        id: 'basic-clean-middle',
        name: 'Clean White Middle',
        description: '흰 글자 + 검정 외곽선, 중앙형',
        layoutId: 'basic',
        themeId: 'modern_blue',
        version: '1.0.0',
        layout: {
            x: 0,
            y: 0,
            width: 1080,
            height: 1920,
            scale: 1,
            objectFit: 'contain',
        },
        textStyle: {
            fontFamily: 'Gothic A1',
            fontSize: 68,
            width: 1080,
            color: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0)',
            captionY: 0.5,
            stroke: {
                width: 6,
                color: '#000000',
            },
        }
    },
    {
        id: 'basic-clean-serif',
        name: 'Clean White Serif',
        description: '명조 계열 흰 자막, 배경 없음',
        layoutId: 'basic',
        themeId: 'modern_blue',
        version: '1.0.0',
        layout: {
            x: 0,
            y: 0,
            width: 1080,
            height: 1920,
            scale: 1,
            objectFit: 'contain',
        },
        textStyle: {
            fontFamily: 'Nanum Myeongjo',
            fontSize: 74,
            width: 1080,
            color: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0)',
            captionY: 0.74,
            stroke: {
                width: 6,
                color: '#000000',
            },
        }
    },
    {
        id: 'basic-box-classic',
        name: 'Classic Box',
        description: '검정 배경 박스 유지형',
        layoutId: 'basic',
        themeId: 'modern_blue',
        version: '1.0.0',
        layout: {
            x: 0,
            y: 0,
            width: 1080,
            height: 1920,
            scale: 1,
            objectFit: 'contain',
        },
        textStyle: {
            fontFamily: 'Do Hyeon',
            fontSize: 70,
            width: 1080,
            color: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0.88)',
            captionY: 0.8,
            stroke: {
                width: 4,
                color: '#000000',
            },
        }
    },
    {
        id: 'basic-box-top',
        name: 'Top Box Accent',
        description: '상단형 배경 박스 자막',
        layoutId: 'basic',
        themeId: 'modern_blue',
        version: '1.0.0',
        layout: {
            x: 0,
            y: 0,
            width: 1080,
            height: 1920,
            scale: 1,
            objectFit: 'contain',
        },
        textStyle: {
            fontFamily: 'Noto Sans KR',
            fontSize: 66,
            width: 1080,
            color: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            captionY: 0.16,
            stroke: {
                width: 4,
                color: '#000000',
            },
        }
    }
];

const RAW_TEMPLATES: VideoTemplate[] = [
    ...LEGACY_TEMPLATES,
    ...AUTO_GENERATED_TEMPLATES.filter(t =>
        !LEGACY_TEMPLATES.some(legacy => legacy.id === t.id)
    )
];

export const DEFAULT_TEMPLATE_VIGNETTE: VisualEffect = {
    type: 'overlay-effect',
    variant: 'vignette',
    intensity: 0.52,
    size: 0.62,
    softness: 0.68,
    color: '#000000',
    shape: 'elliptical',
};

const BASIC_TEMPLATES = RAW_TEMPLATES.filter((template) => template.layoutId === 'basic');
const fallbackTemplate = BASIC_TEMPLATES[BASIC_TEMPLATES.length - 1] || RAW_TEMPLATES[RAW_TEMPLATES.length - 1] || LEGACY_TEMPLATES[0];

export const DEFAULT_TEMPLATE_ID = getTemplateByPreferredDefault()?.id || fallbackTemplate?.id || 'basic-clean-serif';

function getTemplateByPreferredDefault(): VideoTemplate | undefined {
    return RAW_TEMPLATES.find((template) => template.id === 'basic-clean-serif');
}

export const VIDEO_TEMPLATES: VideoTemplate[] = RAW_TEMPLATES.map((template) => {
    if (template.id !== DEFAULT_TEMPLATE_ID) {
        return template;
    }

    return {
        ...template,
        visualEffects: [
            ...(template.visualEffects || []),
            DEFAULT_TEMPLATE_VIGNETTE,
        ],
    };
});

export { TEMPLATE_LAYOUTS, TEMPLATE_THEMES };
export { getCompatibleThemes, getCompatibleLayouts } from './template-generator';

export function isDefaultTemplateVignetteEffect(effect: VisualEffect | undefined): boolean {
    return (
        effect?.type === 'overlay-effect' &&
        effect.variant === 'vignette' &&
        effect.intensity === DEFAULT_TEMPLATE_VIGNETTE.intensity &&
        effect.size === DEFAULT_TEMPLATE_VIGNETTE.size &&
        effect.softness === DEFAULT_TEMPLATE_VIGNETTE.softness &&
        effect.color === DEFAULT_TEMPLATE_VIGNETTE.color &&
        effect.shape === DEFAULT_TEMPLATE_VIGNETTE.shape
    );
}

export function getTemplateVisualEffects(
    template: VideoTemplate,
    templateOptions?: Partial<TemplateOptions>
): VisualEffect[] | undefined {
    if (template.id !== DEFAULT_TEMPLATE_ID || templateOptions?.vignetteEnabled !== false) {
        return template.visualEffects;
    }

    const filteredEffects = (template.visualEffects || []).filter(
        (effect) => !isDefaultTemplateVignetteEffect(effect)
    );

    return filteredEffects.length > 0 ? filteredEffects : undefined;
}

export function templatePreviewHasVignette(
    template: VideoTemplate,
    templateOptions?: Partial<TemplateOptions>
): boolean {
    return (getTemplateVisualEffects(template, templateOptions) || []).some(
        (effect) => effect.type === 'overlay-effect' && effect.variant === 'vignette'
    );
}

export function getTemplateById(templateId: string): VideoTemplate | undefined {
    return VIDEO_TEMPLATES.find(t => t.id === templateId);
}
